/**
 * requestInventory.js — the sign-off report's "Requests graded" section: every
 * GAM, Prebid and APS request in the run's scope, with its identifying params
 * (GAM: iu, sz, correlator, cust_params key-values; Prebid: imp ids, tagids,
 * sizes, bidders; APS: slots and sizes) and which checks failed or warned on it.
 * Answers "51 GAM requests, 2 failed — which ones and why" without the reader
 * opening the dashboard. Pure, never throws.
 */

export const INVENTORY_FAMILIES = [
  { key: 'gam', label: 'GAM ad requests', types: ['google'] },
  { key: 'prebid', label: 'Prebid auction requests', types: ['prebid'] },
  { key: 'aps', label: 'Amazon APS requests', types: ['amazon', 'amazon_video'] },
];
export const INVENTORY_ROW_CAP = 500;

const typeOf = (r) => (r && (r.requestType || r.type)) || null;
const idsOf = (r) => [r?.id, r?.requestId, r?._k].filter((v) => v !== undefined && v !== null && v !== '').map(String);
// Keys that name ONE request. `id` is the ad slot for GAM AND Prebid rows (gpid ==
// iu), so joining failures on it stamped "GAM request clean" onto every Xandr row of
// the same slot. Use requestId/_k; fall back to `id` only when a row has neither.
const rowKeysOf = (r) => {
  const own = [r?.requestId, r?._k].filter((v) => v !== undefined && v !== null && v !== '').map(String);
  return own.length ? own : idsOf(r);
};

function query(url) {
  try { return new URL(String(url)).searchParams; } catch { return new URLSearchParams(); }
}
function parseJson(body) {
  if (!body) return null;
  if (typeof body === 'object') return body;
  try { return JSON.parse(body); } catch { return null; }
}

export function gamParams(row) {
  const q = query(row?.url);
  let kvps = {};
  try { kvps = Object.fromEntries(new URLSearchParams(decodeURIComponent(q.get('cust_params') || ''))); } catch { kvps = {}; }
  return {
    iu: row?.validData?.adUnit || q.get('iu') || q.get('iu_parts') || null,
    sz: q.get('sz') || q.get('prev_iu_szs') || null,
    correlator: row?.validData?.correlator || q.get('correlator') || q.get('c') || null,
    kvps,
  };
}

export function prebidParams(row) {
  const ortb = parseJson(row?.requestBody);
  const imps = Array.isArray(ortb?.imp) ? ortb.imp : [];
  const sizes = new Set(); const bidders = new Set(); const impIds = []; const tagids = [];
  for (const imp of imps) {
    if (imp?.id != null) impIds.push(String(imp.id));
    if (imp?.tagid) tagids.push(String(imp.tagid));
    for (const f of (imp?.banner?.format || [])) if (f?.w && f?.h) sizes.add(`${f.w}x${f.h}`);
    if (imp?.banner?.w && imp?.banner?.h) sizes.add(`${imp.banner.w}x${imp.banner.h}`);
    if (imp?.video?.w && imp?.video?.h) sizes.add(`${imp.video.w}x${imp.video.h} video`);
    const pb = imp?.ext?.prebid?.bidder || imp?.ext;
    if (pb && typeof pb === 'object') for (const k of Object.keys(pb)) if (!['prebid', 'gpid', 'data', 'tid', 'ae', 'skadn'].includes(k)) bidders.add(k);
  }
  // Body gone (browser keeps bodies for the newest 300 rows only): the proxy parsed
  // imps/bids into validData.tables at capture time, and that survives the trim.
  if (!ortb) {
    const t = row?.validData?.tables || {};
    for (const imp of (Array.isArray(t.imps) ? t.imps : [])) {
      if (imp?.id != null) impIds.push(String(imp.id));
      const tag = imp?.tagid || imp?.gpid; if (tag) tagids.push(String(tag));
      for (const z of [].concat(imp?.sizes || imp?.size || [])) if (z) sizes.add(String(z));
    }
    for (const b of (Array.isArray(t.bids) ? t.bids : [])) { const seat = b?.seat || b?.bidder; if (seat) bidders.add(String(seat)); }
  }
  const fromTables = !ortb && (impIds.length || tagids.length || bidders.size);
  return { requestId: ortb?.id || null, impIds, tagids, sizes: [...sizes], bidders: [...bidders], bodyMissing: !ortb && !fromTables, usb: row?.captureSource === 'logcat' };
}

export function apsParams(row) {
  const body = parseJson(row?.requestBody);
  const slots = []; const sizes = new Set();
  for (const s of (Array.isArray(body?.slots) ? body.slots : [])) {
    const id = s?.slotID || s?.slotId || s?.slot || s?.sd;
    if (id) slots.push(String(id));
    for (const z of (s?.sizes || s?.size || [])) sizes.add(Array.isArray(z) ? z.join('x') : String(z));
  }
  const q = query(row?.url);
  if (!slots.length && q.get('slots')) slots.push(q.get('slots'));
  return { slots, sizes: [...sizes], bodyMissing: !body };
}

/** Map every row id → [{ checkId, displayName, status }] for checks that failed/warned ON that row. */
export function problemsByRow(resultsByCheckId, checkDefsById) {
  const out = new Map();
  const entries = resultsByCheckId instanceof Map ? [...resultsByCheckId.entries()] : Object.entries(resultsByCheckId || {});
  for (const [checkId, result] of entries) {
    const status = result?.status;
    if (status !== 'fail' && status !== 'warn') continue;
    const def = checkDefsById instanceof Map ? checkDefsById.get(checkId) : checkDefsById?.[checkId];
    const name = def?.displayName || checkId;
    const rows = [
      ...(Array.isArray(result?.failures) ? result.failures.map((f) => f?.row) : []),
      ...(Array.isArray(result?.rows) ? result.rows.filter((r) => r && r.ok === false) : []),
    ];
    const seen = new Set();
    for (const r of rows) {
      for (const id of rowKeysOf(r)) {
        if (seen.has(id)) continue; seen.add(id);
        const list = out.get(id) || []; list.push({ checkId, displayName: name, status }); out.set(id, list);
      }
    }
  }
  return out;
}

export function buildRequestInventory(rows, resultsByCheckId, checkDefsById, winners = null) {
  try {
    const list = Array.isArray(rows) ? rows : [];
    if (!list.length) return [];
    const problems = problemsByRow(resultsByCheckId, checkDefsById);
    return INVENTORY_FAMILIES.map((fam) => {
      // APS's device-info sync (/api3/update_dev_info) is not a bid request.
      const mine = list.filter((r) => fam.types.includes(typeOf(r))
        && !(fam.key === 'aps' && /\/api3\/update_dev_info\b/.test(String(r.url || ''))));
      const items = mine.map((r) => {
        const seen = new Set(); const probs = [];
        for (const id of rowKeysOf(r)) for (const p of (problems.get(id) || [])) if (!seen.has(p.checkId)) { seen.add(p.checkId); probs.push(p); }
        const status = probs.some((p) => p.status === 'fail') ? 'fail' : probs.length ? 'warn' : 'pass';
        const params = fam.key === 'gam' ? gamParams(r) : fam.key === 'prebid' ? prebidParams(r) : apsParams(r);
        const winner = (fam.key === 'gam' && winners && typeof winners.get === 'function')
          ? ([r?._k, r?.id, r?.requestId].map((k) => (k == null ? null : winners.get(String(k)))).find(Boolean) || null)
          : null;
        return { winner, id: idsOf(r)[0] || null, ts: r?.ts ?? r?.startedAt ?? r?.timestamp ?? null, host: hostOf(r?.url), captureSource: r?.captureSource || null, status, problems: probs, params };
      });
      const wonBy = {};
      for (const i of items) if (i.winner) wonBy[i.winner.label] = (wonBy[i.winner.label] || 0) + 1;
      return {
        wonBy,
        key: fam.key, label: fam.label, total: items.length,
        failed: items.filter((i) => i.status === 'fail').length,
        warned: items.filter((i) => i.status === 'warn').length,
        items: items.slice(0, INVENTORY_ROW_CAP), truncated: Math.max(0, items.length - INVENTORY_ROW_CAP),
      };
    }).filter((f) => f.total > 0);
  } catch {
    return [];
  }
}

function hostOf(url) { try { return new URL(String(url)).hostname; } catch { return null; } }
